// How a saved run is described on the Continue card.
//
// Its own module because `playedText` is tested (`modeMenu.test.ts`) and the
// boundaries are the whole of what is worth testing here. `ModeMenu` re-exports
// it, so the test's import is unchanged.

import type { CatalogEntry } from "src/api";

/** "four minutes ago", roughly. A save's age is a hint, not a timestamp. */
export function sinceText(at: number): string {
  const minutes = Math.max(0, Math.round((Date.now() - at) / 60000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

/**
 * How long has been spent in this run, all told.
 *
 * **Rounded up to a minute, never "0m".** A run five moves old has had time
 * spent on it, and a card reporting nought reads as the number being broken
 * rather than as the run being young — which is the same reason `sinceText`
 * says "just now" instead of "0 minutes ago".
 *
 * Hours and minutes rather than a single unit past an hour: "2h" throws away
 * the half hour somebody spent, and this is the one number on the card that is
 * *theirs* rather than the board's.
 */
export function playedText(ms: number): string {
  const minutes = Math.max(1, Math.round(ms / 60000));
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
}

/**
 * How hard the saved run was set to get, when the save knows.
 *
 * Saves written before difficulty was kept have none, and inventing one for
 * them would be the card asserting something it cannot know. `difficulties`
 * is `catalog.difficulties()`, handed in so this stays a pure function.
 */
export function difficultyText(difficulties: readonly CatalogEntry[], level?: string): string {
  const named = difficulties.find((entry) => entry.id === level);
  return named ? ` · up to ${named.name}` : "";
}
